const SITE_BASE_URL = 'https://botspot.trade';
const BROKER_CONNECTIONS_ROUTE = '/account-settings/broker-connections';

const BROKER_CONNECTION_SOURCE = [
  {
    id: 'alpaca',
    name: 'Alpaca',
    assetClasses: ['Stocks', 'Options', 'Crypto'],
    tags: ['Stocks', 'Options', 'Crypto', 'OAuth', 'API keys'],
    modes: ['paper', 'live'],
    authMethods: ['oauth', 'api_key'],
    promoted: true,
    docsUrl: 'https://docs.alpaca.markets/us/docs/trading-api',
    credentialFields: [
      { key: 'ALPACA_API_KEY', label: 'API Key' },
      { key: 'ALPACA_API_SECRET', label: 'API Secret', secret: true },
    ],
  },
  {
    id: 'schwab',
    name: 'Charles Schwab',
    assetClasses: ['Stocks', 'Options'],
    tags: ['Stocks', 'Options', 'OAuth', 'Live only'],
    modes: ['live'],
    authMethods: ['oauth'],
    docsUrl: 'https://developer.schwab.com/',
    credentialFields: [],
  },
  {
    id: 'tradier',
    name: 'Tradier',
    assetClasses: ['Stocks', 'Options'],
    tags: ['Stocks', 'Options', 'OAuth', 'API keys'],
    modes: ['paper', 'live'],
    modesByAuthMethod: {
      oauth: ['live'],
      api_key: ['paper', 'live'],
    },
    authMethods: ['oauth', 'api_key'],
    docsUrl: 'https://docs.tradier.com/docs/trading',
    referralNote: 'Open a Tradier account through Lumiwealth to qualify for the current cash bonus offer.',
    referralOffer: {
      headline: 'Get a Tradier cash bonus through Lumiwealth',
      description: 'Open through Lumiwealth and deposit $5,000 for a $100 cash bonus or $10,000 for a $300 cash bonus.',
      url: 'https://trade.tradier.com/lumiwealth/',
      ctaLabel: 'Open your account here',
      verifiedAt: '2026-07-02',
    },
    credentialFields: [
      { key: 'TRADIER_ACCESS_TOKEN', label: 'API Key', secret: true },
      { key: 'TRADIER_ACCOUNT_NUMBER', label: 'Account Number' },
    ],
  },
  {
    id: 'tradovate',
    name: 'Tradovate',
    assetClasses: ['Futures'],
    tags: ['Futures', 'API keys'],
    modes: ['paper', 'live'],
    authMethods: ['api_key'],
    docsUrl: 'https://api-d.tradovate.com/',
    credentialFields: [
      { key: 'TRADOVATE_USERNAME', label: 'Username' },
      { key: 'TRADOVATE_DEDICATED_PASSWORD', label: 'Password', secret: true },
      { key: 'TRADOVATE_CID', label: 'CID' },
      { key: 'TRADOVATE_SECRET', label: 'Secret', secret: true },
    ],
  },
  {
    id: 'kraken',
    name: 'Kraken',
    assetClasses: ['Crypto'],
    tags: ['Crypto', 'API keys'],
    modes: ['live'],
    authMethods: ['api_key'],
    docsUrl: 'https://docs.kraken.com/',
    credentialFields: [
      { key: 'KRAKEN_API_KEY', label: 'API Key' },
      { key: 'KRAKEN_API_SECRET', label: 'API Secret', secret: true },
    ],
  },
  {
    id: 'coinbase',
    name: 'Coinbase',
    assetClasses: ['Crypto'],
    tags: ['Crypto', 'API keys'],
    modes: ['live'],
    authMethods: ['api_key'],
    docsUrl: 'https://www.coinbase.com/developer-platform/products/advanced-trade-api',
    credentialFields: [
      { key: 'COINBASE_API_KEY_NAME', label: 'API Key Name' },
      { key: 'COINBASE_PRIVATE_KEY', label: 'Private Key', secret: true, multiline: true },
    ],
  },
  {
    id: 'weex',
    name: 'WEEX',
    assetClasses: ['Crypto'],
    tags: ['Crypto', 'API keys'],
    modes: ['live'],
    authMethods: ['api_key'],
    docsUrl: 'https://www.weex.com/api-doc/spot/introduction/APIBriefIntroduction',
    warning: 'WEEX terms restrict US and Canada eligibility.',
    credentialFields: [
      { key: 'WEEX_API_KEY', label: 'API Key' },
      { key: 'WEEX_API_SECRET', label: 'API Secret', secret: true },
      { key: 'WEEX_API_PASSPHRASE', label: 'API Passphrase', secret: true },
    ],
  },
  {
    id: 'bitunix',
    name: 'Bitunix',
    assetClasses: ['Crypto'],
    tags: ['Crypto', 'API keys'],
    modes: ['live'],
    authMethods: ['api_key'],
    docsUrl: 'https://www.bitunix.com/api-docs/',
    warning: 'Use a scoped API key with withdrawals disabled.',
    credentialFields: [
      { key: 'BITUNIX_API_KEY', label: 'API Key' },
      { key: 'BITUNIX_API_SECRET', label: 'API Secret', secret: true },
    ],
  },
];

const PROJECTX_FIRM_SOURCE = [
  {
    id: 'projectx-topstepx',
    name: 'TopstepX',
    envPrefix: 'TOPSTEPX',
    logoText: 'TopstepX',
    assetClasses: ['Futures'],
    tags: ['Futures', 'Project X', 'API keys'],
    modes: ['paper', 'live'],
    authMethods: ['api_key'],
    docsUrl: 'https://help.topstep.com/en/articles/11187768-topstepx-api-access',
    credentialFields: [
      { key: 'PROJECTX_TOPSTEPX_API_KEY', label: 'API Key' },
      { key: 'PROJECTX_TOPSTEPX_USERNAME', label: 'Username' },
      { key: 'PROJECTX_TOPSTEPX_PREFERRED_ACCOUNT_NAME', label: 'Preferred Account Name' },
    ],
    providerMetadata: { projectxFirm: 'TOPSTEPX' },
  },
];

const ALL_BROKER_CONNECTION_SOURCE = [
  ...BROKER_CONNECTION_SOURCE,
  ...PROJECTX_FIRM_SOURCE,
];

function buildBrokerConnectPath(brokerId, options = {}) {
  const params = new URLSearchParams();
  params.set('action', 'add');
  params.set('broker', brokerId);
  if (options.authMethod) {
    params.set('authMethod', options.authMethod);
  }
  return `${BROKER_CONNECTIONS_ROUTE}?${params.toString()}`;
}

function buildAbsoluteSiteUrl(pathname, baseUrl = SITE_BASE_URL) {
  return new URL(pathname, baseUrl).toString();
}

function buildBrokerConnectUrl(brokerId, options = {}) {
  return buildAbsoluteSiteUrl(buildBrokerConnectPath(brokerId, options), options.baseUrl);
}

function brokerManifestEntry(broker, baseUrl = SITE_BASE_URL) {
  const entry = {
    id: broker.id,
    name: broker.name,
    assetClasses: broker.assetClasses,
    tags: broker.tags,
    modes: broker.modes,
    modesByAuthMethod: broker.modesByAuthMethod || null,
    authMethods: broker.authMethods,
    warning: broker.warning || null,
    docsUrl: broker.docsUrl || null,
    referralNote: broker.referralNote || null,
  };
  if (broker.referralOffer) {
    entry.referralOffer = broker.referralOffer;
  }
  entry.credentialFields = (broker.credentialFields || []).map((field) => ({
    key: field.key,
    label: field.label,
    secret: Boolean(field.secret),
    multiline: Boolean(field.multiline),
  }));
  entry.providerMetadata = broker.providerMetadata || null;
  entry.connectPath = buildBrokerConnectPath(broker.id);
  entry.connectUrl = buildBrokerConnectUrl(broker.id, { baseUrl });
  entry.authConnectUrls = Object.fromEntries(
    (broker.authMethods || []).map((authMethod) => [
      authMethod,
      buildBrokerConnectUrl(broker.id, { authMethod, baseUrl }),
    ])
  );
  return entry;
}

function findBrokerSource(id) {
  return ALL_BROKER_CONNECTION_SOURCE.find((broker) => broker.id === id) || ALL_BROKER_CONNECTION_SOURCE[0];
}

module.exports = {
  ALL_BROKER_CONNECTION_SOURCE,
  BROKER_CONNECTIONS_ROUTE,
  BROKER_CONNECTION_SOURCE,
  PROJECTX_FIRM_SOURCE,
  SITE_BASE_URL,
  brokerManifestEntry,
  buildAbsoluteSiteUrl,
  buildBrokerConnectPath,
  buildBrokerConnectUrl,
  findBrokerSource,
};
